import React, { useMemo } from 'react';
import {
  ComposedChart, Area, Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  ReferenceLine, ReferenceArea, Legend,
} from 'recharts';
import { Gauge, TrendingDown, AlertTriangle, Scale } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import { EMPTY_ARRAY } from '../../store/stableEmpties';
import { MARKET_EQUILIBRIUM_MAX_GROSS_MARGIN } from '../../store/helpers/marketEquilibrium';

const AXIS_TICK = { fontSize: 10, fill: '#64748B' };
const TOOLTIP_STYLE = { backgroundColor: '#0F172A', border: '1px solid #283556', borderRadius: 8, fontSize: 11 };
const CAP_PCT = Math.round(MARKET_EQUILIBRIUM_MAX_GROSS_MARGIN * 100);
const WINDOW_DAYS = 90;
// Marges négatives possibles (contrat déficitaire) : le bas de l'axe suit les données par pas de 25.
const PCT_DOMAIN = [(min) => Math.min(0, Math.floor(min / 25) * 25), 100];

/** Résumé pur de la fenêtre glissante (testable sans React). */
export function summarizeMarginHistory(history, capPct = CAP_PCT, windowDays = WINDOW_DAYS) {
  const win = (history || []).slice(-windowDays);
  if (win.length === 0) return null;
  const last = win[win.length - 1];
  const avg = win.reduce((s, h) => s + (h.grossMarginPct || 0), 0) / win.length;
  const daysOver = win.filter((h) => (h.grossMarginPct || 0) > capPct).length;
  const peak = win.reduce((m, h) => Math.max(m, h.grossMarginPct || 0), -Infinity);
  return {
    current: last.grossMarginPct,
    average: Math.round(avg * 10) / 10,
    daysOver,
    peak,
    pressuredNow: last.pressuredCount || 0,
    foregoneNow: last.foregone || 0,
    contracts: last.contracts || 0,
    tension: (last.grossMarginPct || 0) > capPct ? 'high' : (last.grossMarginPct || 0) > capPct - 10 ? 'warm' : 'calm',
  };
}

const KpiCard = ({ icon: Icon, label, value, sub, tone = 'text-foreground', testId }) => (
  <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${tone}`} />
      <span className="text-xs text-slate-500">{label}</span>
    </div>
    <p className={`text-xl font-bold font-mono ${tone}`}>{value}</p>
    {sub && <p className="text-[10px] text-slate-500 mt-1">{sub}</p>}
  </div>
);

export default function MarketTab() {
  const { t } = useTranslation();
  const marginHistory = useGameStore((s) => s.marginHistory || EMPTY_ARRAY);
  const summary = useMemo(() => summarizeMarginHistory(marginHistory), [marginHistory]);
  const chartData = useMemo(() => marginHistory.slice(-WINDOW_DAYS).map((h) => ({
    day: `J${h.day}`,
    marge: h.grossMarginPct,
    renegocies: h.pressuredCount || 0,
    contrats: h.contracts || 0,
  })), [marginHistory]);

  if (!summary) {
    return (
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-6 text-center" data-testid="market-tab-empty">
        <Scale className="w-8 h-8 text-slate-600 mx-auto mb-2" />
        <p className="text-sm text-slate-400">{t('market_tab_empty')}</p>
        <p className="text-[11px] text-slate-500 mt-1">{t('market_tab_empty_hint', { cap: CAP_PCT })}</p>
      </div>
    );
  }

  const toneFor = summary.tension === 'high' ? 'text-orange-300' : summary.tension === 'warm' ? 'text-amber-300' : 'text-money';
  const tensionLabel = t(`market_tension_${summary.tension}`);

  return (
    <div className="space-y-4" data-testid="market-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Gauge} label={t('market_kpi_current')} value={`${summary.current} %`} sub={`${t('market_kpi_cap')} ${CAP_PCT} % · ${tensionLabel}`} tone={toneFor} testId="market-kpi-current" />
        <KpiCard icon={Scale} label={t('market_kpi_average')} value={`${summary.average} %`} sub={t('market_kpi_average_sub', { days: Math.min(WINDOW_DAYS, marginHistory.length), peak: summary.peak })} testId="market-kpi-average" />
        <KpiCard icon={AlertTriangle} label={t('market_kpi_days_over')} value={`${summary.daysOver} j`} sub={t('market_kpi_days_over_sub', { cap: CAP_PCT })} tone={summary.daysOver > 0 ? 'text-orange-300' : 'text-foreground'} testId="market-kpi-days-over" />
        <KpiCard icon={TrendingDown} label={t('market_kpi_renegotiated')} value={`${summary.pressuredNow}/${summary.contracts}`} sub={t('market_kpi_renegotiated_sub', { amount: formatMoney(summary.foregoneNow) })} tone={summary.pressuredNow > 0 ? 'text-orange-300' : 'text-foreground'} testId="market-kpi-renegotiated" />
      </div>

      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid="market-margin-chart">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div>
            <h4 className="font-bold text-slate-200 text-sm">{t('market_chart_title')}</h4>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-mono">{t('market_chart_subtitle', { days: WINDOW_DAYS, cap: CAP_PCT })}</p>
          </div>
          <span className={`text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-1 rounded-md border ${summary.tension === 'high' ? 'bg-orange-500/15 text-orange-300 border-orange-500/40' : summary.tension === 'warm' ? 'bg-amber-500/15 text-amber-300 border-amber-500/40' : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'}`} data-testid="market-tension-level">
            {tensionLabel}
          </span>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <defs>
              <linearGradient id="marginFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#38BDF8" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1A233A" />
            <XAxis dataKey="day" tick={AXIS_TICK} stroke="#283556" minTickGap={24} />
            <YAxis yAxisId="pct" domain={PCT_DOMAIN} tickCount={6} allowDecimals={false} tick={AXIS_TICK} stroke="#283556" unit="%" />
            <YAxis yAxisId="count" orientation="right" allowDecimals={false} tick={AXIS_TICK} stroke="#283556" />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, name) => (name === t('market_series_margin') ? [`${v} %`, name] : [v, name])} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceArea yAxisId="pct" y1={CAP_PCT} y2={100} fill="#F97316" fillOpacity={0.08} />
            <ReferenceLine yAxisId="pct" y={CAP_PCT} stroke="#EF4444" strokeDasharray="6 4" strokeWidth={1.5} label={{ value: `${t('market_kpi_cap')} ${CAP_PCT} %`, position: 'insideTopRight', fill: '#EF4444', fontSize: 10 }} />
            <Bar yAxisId="count" dataKey="renegocies" name={t('market_series_renegotiated')} fill="#F97316" fillOpacity={0.55} barSize={6} radius={[2, 2, 0, 0]} />
            <Area yAxisId="pct" type="monotone" dataKey="marge" name={t('market_series_margin')} stroke="#38BDF8" strokeWidth={2} fill="url(#marginFill)" dot={false} activeDot={{ r: 3 }} />
            <Line yAxisId="count" type="stepAfter" dataKey="contrats" name={t('market_series_contracts')} stroke="#64748B" strokeWidth={1} dot={false} strokeDasharray="2 3" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-ink-800/60 rounded-lg border border-ink-600 p-4 text-[11px] text-slate-400 leading-relaxed" data-testid="market-tab-explainer">
        <p className="font-bold text-slate-300 mb-1">{t('market_explainer_title')}</p>
        <p>{t('market_explainer_body', { cap: CAP_PCT })}</p>
      </div>
    </div>
  );
}
